import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { 
  Award, 
  CheckCircle,
  Video,
  MapPin,
  PhoneCall,
  Star,
  Calendar,
  Briefcase,
  Heart,
  Clock,
  Sparkles,
  Languages,
  Shield
} from 'lucide-react';
import QRCode from 'react-qr-code';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full photo URL
const getPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

const modalityLabels = {
  onsite: 'On-Site',
  remote_video: 'Video Remote',
  remote_phone: 'Phone Remote'
};

const modalityIcons = {
  onsite: MapPin,
  remote_video: Video,
  remote_phone: PhoneCall
};

// Top 20 languages spoken in the US
const languageNames = {
  'en': 'English',
  'es': 'Spanish', 
  'zh': 'Chinese',
  'zh-yue': 'Cantonese',
  'tl': 'Tagalog',
  'vi': 'Vietnamese',
  'ar': 'Arabic',
  'fr': 'French',
  'ko': 'Korean',
  'ru': 'Russian',
  'de': 'German',
  'ht': 'Haitian Creole',
  'hi': 'Hindi',
  'pt': 'Portuguese',
  'it': 'Italian',
  'pl': 'Polish',
  'ja': 'Japanese',
  'ur': 'Urdu',
  'fa': 'Farsi/Persian',
  'gu': 'Gujarati',
  'te': 'Telugu',
  'hmn': 'Hmong',
  'so': 'Somali',
  'my': 'Burmese',
  'ti': 'Tigrinya',
  'ne': 'Nepali',
  'sw': 'Swahili',
  'bn': 'Bengali',
  'pa': 'Punjabi',
  'ta': 'Tamil'
};

const getLanguageName = (code) => languageNames[code?.toLowerCase()] || code;

const getFirstNameAndInitial = (fullName) => {
  if (!fullName) return { firstName: 'Interpreter', initial: '' };
  const parts = fullName.trim().split(' ');
  if (parts.length === 1) return { firstName: parts[0], initial: '' };
  return { 
    firstName: parts[0], 
    initial: parts[parts.length - 1][0].toUpperCase() + '.'
  };
};

const VendorPublicProfile = () => {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const profileUrl = typeof window !== 'undefined' ? window.location.href : '';

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/vendor-profile/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setProfile(data);
        } else if (response.status === 404) {
          setError('Profile not found');
        } else {
          setError('Failed to load profile');
        }
      } catch (err) {
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-2xl p-8 text-center max-w-md">
          <div className="w-20 h-20 mx-auto bg-slate-700 rounded-full flex items-center justify-center mb-4">
            <Shield className="h-10 w-10 text-slate-500" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">{error}</h2>
          <p className="text-slate-400">This profile may have been removed or is not available.</p>
        </div>
      </div>
    );
  }

  const { firstName, initial } = getFirstNameAndInitial(profile.name);
  const yearsExperience = profile.years_experience || Math.floor((profile.completed_assignments || 0) / 50) + 1;

  return (
    <div className="min-h-screen bg-slate-900" data-testid="vendor-public-profile">
      {/* Hero Section with ID Badge Style */}
      <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-600 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-12 relative z-10">
          {/* ID Badge Card */}
          <div className="bg-gradient-to-br from-[#0a1628] to-[#162544] rounded-3xl shadow-2xl overflow-hidden border border-slate-700/50">
            {/* Badge Header Strip */}
            <div className="bg-gradient-to-r from-orange-500 via-orange-400 to-orange-500 h-2"></div>
            
            {/* Badge Content */}
            <div className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-center gap-8">
                {/* Photo Section */}
                <div className="relative">
                  <div className="w-48 h-48 rounded-2xl overflow-hidden bg-slate-700 border-4 border-orange-500 shadow-xl shadow-orange-500/20">
                    {profile.profile_photo_url ? (
                      <img 
                        src={getPhotoUrl(profile.profile_photo_url)} 
                        alt={firstName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center">
                        <span className="text-7xl font-black text-orange-500">
                          {firstName.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Verified Badge */}
                  <div className="absolute -bottom-2 -right-2 bg-orange-500 rounded-full p-2 shadow-lg">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Name & Info Section */}
                <div className="text-center md:text-left flex-1">
                  <div className="mb-4">
                    <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight">
                      {firstName}
                      <span className="text-orange-500"> {initial}</span>
                    </h1>
                  </div>
                  
                  <p className="text-xl text-slate-300 mb-4 font-medium">
                    {profile.tagline || 'Professional Language Specialist'}
                  </p>

                  {/* Vendor Type Badge */}
                  <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/50 rounded-full px-5 py-2">
                    <Sparkles className="w-5 h-5 text-orange-400" />
                    <span className="text-orange-400 font-bold uppercase tracking-wide text-sm">
                      {profile.vendor_type === 'interpreter' ? 'Certified Interpreter' :
                       profile.vendor_type === 'translator' ? 'Certified Translator' :
                       'Interpreter & Translator'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Bar */}
            <div className="bg-slate-800/50 border-t border-slate-700/50">
              <div className="grid grid-cols-3 divide-x divide-slate-700/50">
                <div className="p-6 text-center">
                  <Briefcase className="w-6 h-6 mx-auto text-orange-500 mb-2" />
                  <p className="text-3xl font-black text-white">{profile.completed_assignments || 0}+</p>
                  <p className="text-sm text-slate-400 font-medium uppercase tracking-wide">Assignments</p>
                </div>
                <div className="p-6 text-center">
                  <Calendar className="w-6 h-6 mx-auto text-orange-500 mb-2" />
                  <p className="text-3xl font-black text-white">{yearsExperience}</p>
                  <p className="text-sm text-slate-400 font-medium uppercase tracking-wide">Years Exp.</p>
                </div>
                <div className="p-6 text-center">
                  <Star className="w-6 h-6 mx-auto text-orange-500 mb-2" />
                  <p className="text-3xl font-black text-white">{profile.reliability_score?.toFixed(0) || 100}%</p>
                  <p className="text-sm text-slate-400 font-medium uppercase tracking-wide">Rating</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid gap-6">
          
          {/* Location & Availability */}
          {(profile.city || profile.availability) && (
            <div className="flex flex-wrap gap-4 justify-center">
              {profile.city && (
                <div className="flex items-center gap-3 bg-slate-800 px-6 py-3 rounded-xl border border-slate-700">
                  <MapPin className="w-5 h-5 text-orange-500" />
                  <span className="text-white font-semibold">{profile.city}</span>
                </div>
              )}
              {profile.availability && (
                <div className="flex items-center gap-3 bg-green-900/30 px-6 py-3 rounded-xl border border-green-700/50">
                  <Clock className="w-5 h-5 text-green-400" />
                  <span className="text-green-300 font-semibold">{profile.availability}</span>
                </div>
              )}
            </div>
          )}

          {/* Bio */}
          {profile.bio && (
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
              <p className="text-xl text-slate-300 text-center leading-relaxed italic">
                "{profile.bio}"
              </p>
            </div>
          )}

          {/* Language Pairs */}
          {profile.language_pairs && profile.language_pairs.length > 0 && (
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
              <div className="flex items-center gap-3 mb-6 justify-center">
                <Languages className="w-6 h-6 text-orange-500" />
                <h3 className="text-xl font-bold text-white uppercase tracking-wide">Language Pairs</h3>
              </div>
              <div className="flex flex-wrap gap-3 justify-center">
                {profile.language_pairs.map((pair, idx) => (
                  <div 
                    key={idx} 
                    className="bg-gradient-to-r from-blue-900/50 to-slate-800 px-5 py-3 rounded-xl border border-blue-700/50"
                  >
                    <span className="text-white font-bold">
                      {getLanguageName(pair.source_language)} 
                      <span className="text-orange-400 mx-2">↔</span> 
                      {getLanguageName(pair.target_language)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Legacy Languages */}
          {(!profile.language_pairs || profile.language_pairs.length === 0) && profile.languages && profile.languages.length > 0 && (
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
              <div className="flex items-center gap-3 mb-6 justify-center">
                <Languages className="w-6 h-6 text-orange-500" />
                <h3 className="text-xl font-bold text-white uppercase tracking-wide">Languages</h3>
              </div>
              <div className="flex flex-wrap gap-3 justify-center">
                {profile.languages.map((lang, idx) => (
                  <Badge 
                    key={idx} 
                    className="bg-blue-900/50 text-blue-300 border-blue-700/50 px-4 py-2 text-base font-semibold"
                  >
                    {getLanguageName(lang)}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Service Types */}
          {profile.modalities && profile.modalities.length > 0 && (
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
              <div className="flex items-center gap-3 mb-6 justify-center">
                <CheckCircle className="w-6 h-6 text-orange-500" />
                <h3 className="text-xl font-bold text-white uppercase tracking-wide">Available For</h3>
              </div>
              <div className="flex flex-wrap gap-4 justify-center">
                {profile.modalities.map((modality, idx) => {
                  const Icon = modalityIcons[modality] || CheckCircle;
                  return (
                    <div 
                      key={idx} 
                      className="flex items-center gap-3 bg-slate-700/50 px-5 py-3 rounded-xl border border-slate-600"
                    >
                      <Icon className="w-5 h-5 text-orange-500" />
                      <span className="text-white font-semibold">{modalityLabels[modality] || modality}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Specialties */}
          {profile.specialties && profile.specialties.length > 0 && (
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
              <div className="flex items-center gap-3 mb-6 justify-center">
                <Briefcase className="w-6 h-6 text-orange-500" />
                <h3 className="text-xl font-bold text-white uppercase tracking-wide">Specialties</h3>
              </div>
              <div className="flex flex-wrap gap-3 justify-center">
                {profile.specialties.map((spec, idx) => (
                  <Badge 
                    key={idx} 
                    className="bg-orange-500/20 text-orange-300 border-orange-500/50 px-4 py-2 text-base font-semibold capitalize"
                  >
                    {spec}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Certifications */}
          {profile.credentials && profile.credentials.length > 0 && (
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
              <div className="flex items-center gap-3 mb-6 justify-center">
                <Award className="w-6 h-6 text-orange-500" />
                <h3 className="text-xl font-bold text-white uppercase tracking-wide">Certifications</h3>
              </div>
              <div className="grid gap-3 max-w-lg mx-auto">
                {profile.credentials.map((cred, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center gap-4 bg-gradient-to-r from-yellow-900/30 to-orange-900/30 p-4 rounded-xl border border-yellow-700/50"
                  >
                    <Award className="w-8 h-8 text-yellow-500 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-white">{cred.name}</p>
                      {cred.issuer && (
                        <p className="text-sm text-slate-400">{cred.issuer}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Favorite Assignments */}
          {profile.favorite_assignments && (
            <div className="bg-gradient-to-r from-pink-900/30 to-purple-900/30 rounded-2xl p-8 border border-pink-700/50">
              <div className="flex items-center gap-3 mb-4 justify-center">
                <Heart className="w-6 h-6 text-pink-400" />
                <h3 className="text-xl font-bold text-white uppercase tracking-wide">Favorite Assignments</h3>
              </div>
              <p className="text-center text-pink-200 text-lg font-medium">{profile.favorite_assignments}</p>
            </div>
          )}

          {/* Custom Message */}
          {profile.custom_message && (
            <div className="bg-gradient-to-r from-orange-900/30 to-yellow-900/30 rounded-2xl p-8 border border-orange-700/50">
              <p className="text-center text-orange-200 text-xl italic font-medium">"{profile.custom_message}"</p>
            </div>
          )}

          {/* QR Code */}
          <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
            <h3 className="text-xl font-bold text-white uppercase tracking-wide text-center mb-6">
              Share This Profile
            </h3>
            <div className="flex justify-center">
              <div className="bg-white p-4 rounded-2xl shadow-xl">
                <QRCode 
                  value={profileUrl} 
                  size={150}
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-800/50 border-t border-slate-700/50 py-8">
        <div className="text-center">
          <p className="text-slate-400">
            Professional profile powered by{' '}
            <a 
              href="https://bylyngo.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-orange-400 font-bold hover:text-orange-300 transition-colors"
            >
              Bylyngo Interpreting & Translation
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VendorPublicProfile;
